import {
  CatalogAttribute,
  RimMaterialType,
  WheelFastenerType,
  type CatalogNodeSpecificationRecord,
  type CatalogServiceConditionRecord
} from '@navio/nsdk';
import { formatMeasure } from './gaugeValue';

/**
 * US English for the wheel facts a service needs. `CatalogAttributeLabels` is British ("Tyre
 * pressure", "Centre bore"), so every surface that names an attribute reads this map instead.
 * "Wheel nut torque" is the wording the customer app, the worker app and the ops signals already
 * use; the catalog pages say the same thing rather than inventing a third spelling.
 */
export const CATALOG_ATTRIBUTE_LABEL: Record<CatalogAttribute, string> = {
  [CatalogAttribute.TYRE_PRESSURE]: 'Tire pressure',
  [CatalogAttribute.WHEEL_TORQUE]: 'Wheel nut torque',
  [CatalogAttribute.BOLT_PATTERN]: 'Bolt pattern',
  [CatalogAttribute.CENTRE_BORE]: 'Center bore',
  [CatalogAttribute.THREAD]: 'Thread size'
};

/**
 * US English again, and the words a service uses: `WheelFastenerTypeLabels` is British ("Centre
 * lock") and calls a lug nut a "Nut", which reads as the thread rather than the part on the wheel.
 */
export const WHEEL_FASTENER_LABEL: Record<WheelFastenerType, string> = {
  [WheelFastenerType.BOLT]: 'Bolt',
  [WheelFastenerType.NUT]: 'Lug nut',
  [WheelFastenerType.CENTER_LOCK]: 'Center lock'
};

/** Rows in the order a service reads them: the two that block a visit first. */
const CATALOG_ATTRIBUTE_ORDER: CatalogAttribute[] = [
  CatalogAttribute.TYRE_PRESSURE,
  CatalogAttribute.WHEEL_TORQUE,
  CatalogAttribute.BOLT_PATTERN,
  CatalogAttribute.CENTRE_BORE,
  CatalogAttribute.THREAD
];

export const catalogAttributeOptions = CATALOG_ATTRIBUTE_ORDER.map((attribute) => ({
  value: attribute,
  label: CATALOG_ATTRIBUTE_LABEL[attribute]
}));

/**
 * Rim material stays a code on the record. It is the one qualifier a worker acts on, so it is
 * translated; other qualifier codes are omitted rather than printed as machine names.
 *
 * US English again, and the part rather than the metal: `RimMaterialTypeLabels` is British
 * ("Aluminium") and names the material, which reads as the alloy rather than the wheel.
 */
const RIM_MATERIAL_CONDITION = 'rim_material';

const RIM_MATERIAL_TEXT: Record<RimMaterialType, string> = {
  [RimMaterialType.ALUMINIUM]: 'Alloy rims',
  [RimMaterialType.STEEL]: 'Steel rims'
};

const isRimMaterial = (value: string): value is RimMaterialType => value in RIM_MATERIAL_TEXT;

const sentenceCase = (value: string) => {
  const spaced = value.replaceAll('_', ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * One condition as text. Rim material is the qualifier operators and workers both act on, so it is
 * named; anything else falls back to its code, spaced and sentence-cased.
 *
 * `rimMaterialTexts` lets a translated surface pass its own wording. Worker and customer surfaces
 * that must not print machine names ask for `omitUnknown`, which drops every other qualifier.
 */
export const catalogConditionText = (
  condition: CatalogServiceConditionRecord,
  options?: { rimMaterialTexts?: Record<RimMaterialType, string>; omitUnknown?: boolean }
): string | undefined => {
  if (condition.name === RIM_MATERIAL_CONDITION && isRimMaterial(condition.value)) {
    return options?.rimMaterialTexts?.[condition.value] ?? RIM_MATERIAL_TEXT[condition.value];
  }
  if (options?.omitUnknown) {
    return undefined;
  }
  return `${sentenceCase(condition.name)}: ${sentenceCase(condition.value)}`;
};

// Unknown, unreported and not applicable are stored so a later review can tell them apart. They are
// not facts, so no surface prints them as one.
const HIDDEN_FACT = new Set(['unknown', 'not_reported', 'not_applicable']);

export const shownChoice = (value: string) => !HIDDEN_FACT.has(value);

/** The measurement and its applicability, without provider attribution. */
export const catalogSpecificationValueText = (spec: CatalogNodeSpecificationRecord): string => {
  const value =
    spec.valueNumber == null ? spec.valueText : `${formatMeasure(spec.valueNumber)} ${spec.unit ?? ''}`.trim();
  return [value, ...spec.conditions.map((condition) => catalogConditionText(condition))].filter(Boolean).join(' · ');
};

// Wheel-Size publishes every power figure in kilowatts, mechanical horsepower and metric horsepower.
// The catalog stores the kilowatts -- one measurement, not three -- and these factors reproduce the
// provider's printed hp and PS from it (370 kW -> 496 hp, 503 PS).
const HORSEPOWER_PER_KILOWATT = 1.34102;
const METRIC_HORSEPOWER_PER_KILOWATT = 1.35962;

/** A power figure in the three units the provider shows, the headline hp first. */
export const catalogPowerText = (kilowatts: number): string =>
  [
    `${Math.round(kilowatts * HORSEPOWER_PER_KILOWATT)} hp`,
    `${formatMeasure(kilowatts)} kW`,
    `${Math.round(kilowatts * METRIC_HORSEPOWER_PER_KILOWATT)} PS`
  ].join(' · ');

/** Compact power label for card headers. */
export const catalogPowerSummary = (kilowatts: number): { kilowatts: string; horsepower: string } => ({
  kilowatts: `${formatMeasure(kilowatts)} kW`,
  horsepower: `${Math.round(kilowatts * HORSEPOWER_PER_KILOWATT)} hp`
});
